import {useCallback, useEffect, useRef, useState} from 'react';
import {LiveSessionSummary, liveSessionsApi} from '../../../../services/liveSessionsApi';
import {isRequestCanceled} from '../utils';

interface UseHomeLiveViewerArgs {
  token: string | null;
  onSessionUpdated: (session: LiveSessionSummary) => void;
  onSessionEnded: (sessionId: string) => void;
  refreshLiveSessions: () => Promise<void>;
}

export const useHomeLiveViewer = ({
  token,
  onSessionUpdated,
  onSessionEnded,
  refreshLiveSessions,
}: UseHomeLiveViewerArgs) => {
  const joinRequestControllerRef = useRef<AbortController | null>(null);
  const ignoreDisconnectRef = useRef(false);

  const [isViewerModalOpen, setIsViewerModalOpen] = useState(false);
  const [viewerSession, setViewerSession] = useState<LiveSessionSummary | null>(null);
  const [viewerAccessToken, setViewerAccessToken] = useState<string | null>(null);
  const [viewerLiveKitUrl, setViewerLiveKitUrl] = useState<string | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);

  const resetViewerState = useCallback(() => {
    joinRequestControllerRef.current?.abort();
    setViewerAccessToken(null);
    setViewerLiveKitUrl(null);
    setViewerSession(null);
    setViewerLoading(false);
    setViewerError(null);
    setIsViewerModalOpen(false);
  }, []);

  const openViewerModal = useCallback(async (session: LiveSessionSummary) => {
    if (!token) {
      setViewerError('You must be signed in to watch live sessions.');
      return;
    }

    joinRequestControllerRef.current?.abort();
    ignoreDisconnectRef.current = false;
    setViewerSession(session);
    setViewerAccessToken(null);
    setViewerLiveKitUrl(null);
    setViewerLoading(true);
    setViewerError(null);
    setIsViewerModalOpen(true);

    const controller = new AbortController();
    joinRequestControllerRef.current = controller;

    try {
      const response = await liveSessionsApi.createViewerToken(token, session.id, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      setViewerSession(response.session);
      setViewerAccessToken(response.viewerToken);
      setViewerLiveKitUrl(response.liveKitUrl);
      onSessionUpdated(response.session);
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setViewerError(error instanceof Error ? error.message : 'Failed to join the live session');
    } finally {
      if (joinRequestControllerRef.current === controller) {
        joinRequestControllerRef.current = null;
      }

      setViewerLoading(false);
    }
  }, [onSessionUpdated, token]);

  const closeViewerModal = useCallback(() => {
    ignoreDisconnectRef.current = true;
    resetViewerState();
  }, [resetViewerState]);

  const handleViewerConnected = useCallback(() => {
    setViewerError(null);
  }, []);

  const handleViewerDisconnected = useCallback(() => {
    if (ignoreDisconnectRef.current) {
      ignoreDisconnectRef.current = false;
      return;
    }

    const sessionId = viewerSession?.id;

    setViewerAccessToken(null);
    setViewerLiveKitUrl(null);

    if (sessionId) {
      onSessionEnded(sessionId);
      void refreshLiveSessions();
    }

    setViewerError('This live session is no longer available.');
  }, [onSessionEnded, refreshLiveSessions, viewerSession?.id]);

  useEffect(() => {
    return () => {
      joinRequestControllerRef.current?.abort();
    };
  }, []);

  return {
    isViewerModalOpen,
    viewerSession,
    viewerAccessToken,
    viewerLiveKitUrl,
    viewerLoading,
    viewerError,
    openViewerModal,
    closeViewerModal,
    handleViewerConnected,
    handleViewerDisconnected,
    handleViewerError: (error: Error) => {
      setViewerError(error.message || 'Failed to play the live session');
    },
  };
};
